import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Badge, CardText } from 'reactstrap';

import { APIConfig } from 'src/config';
import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, UNTRADED_ENERGY_KEY } from 'src/util/constants';
import { getTradeDirectionActionAndPreposition } from 'src/util/i18n/helpers';
import { buildTradeRuleMeterLabel, getUntradedContent } from 'src/util/i18n/trade';
import { buildActorTimestampMessage, buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';
import { getPriceRange } from 'src/util/tradeRule';

const messages = defineMessages({
  proposal: {
    id: 'trade_summary.trade_rule_card_text.proposal.details.text',
    defaultMessage: 'Proposed by {actor} on {timestamp}',
  },
  proposalNoActor: {
    id: 'trade_summary.trade_rule_card_text.proposal.details.no_actor.text',
    defaultMessage: 'Proposed on {timestamp}',
  },
  tradeAcceptance: {
    id: 'trade_summary.trade_rule_card_text.trade_acceptance.details.text',
    defaultMessage: 'Accepted by {actor} on {timestamp}',
  },
  tradeAcceptanceNoActor: {
    id: 'trade_summary.trade_rule_card_text.trade_acceptance.details.no_actor.text',
    defaultMessage: 'Accepted on {timestamp}',
  },
});

/**
 * Build the text content that needs to be shown inside the trade rule card.
 * @param {object} props
 * @param {object} props.tradeRule - trade rule object
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} props.direction
 * @param {string} props.type - trade rule type
 * @param {string} props.propertyTitle - property title
 * @returns {React.ReactNode | React.ReactElement | string} - trade rule card text content.
 */
function TradeRuleCardText(props) {
  if (Object.keys(props).length === 0) {
    return null;
  }

  const {
    tradeRule, direction, type, propertyTitle,
  } = props;
  const {
    seller,
    buyer,
    acceptedAt,
    acceptedBy,
    acceptedById,
    proposedAt,
    proposedBy,
    proposedById,
    uuid: ruleId,
  } = tradeRule || {};
  const isBuy = direction === TRADE_DIRECTION_BUY;
  const trader = isBuy ? buyer : seller;
  const counterparty = isBuy ? seller : buyer;
  const priceRange = getPriceRange(tradeRule);
  const { maximum, minimum } = priceRange || {};
  const mode = APIConfig().MODE;
  const intl = useIntl();
  const tradeDirectionActionAndPreposition = getTradeDirectionActionAndPreposition(intl, direction);
  const tradeRuleMeterLabel = buildTradeRuleMeterLabel(
    intl,
    trader,
    counterparty,
    direction,
    true,
  );

  const meterLabelText = intl.formatMessage({
    id: 'trade_summary.trade_rule_card_text.meter_label.details.text',
    defaultMessage: '{tradeDirectionActionAndPreposition} {tradeRuleMeterLabel}',
  }, { tradeDirectionActionAndPreposition, tradeRuleMeterLabel });

  const noMeterLabelText = buildActorTimestampMessage(intl, {
    user: proposedBy,
    userId: proposedById,
    timestamp: proposedAt,
    descriptor: messages.proposal,
    fallbackDescriptor: messages.proposalNoActor,
  });

  const meterLabel = tradeRuleMeterLabel ? meterLabelText : noMeterLabelText;

  const acceptedAtText = buildActorTimestampMessage(intl, {
    user: acceptedBy,
    userId: acceptedById,
    timestamp: acceptedAt,
    descriptor: messages.tradeAcceptance,
    fallbackDescriptor: messages.tradeAcceptanceNoActor,
  });

  if (type === UNTRADED_ENERGY_KEY) {
    return getUntradedContent(intl, propertyTitle);
  }
  return (
    <CardText>
      {meterLabel}
      <br />
      {acceptedAt && (
        <>
          {acceptedAtText}
          <br />
        </>
      )}
      {buildTradeRulePriceInformation({
        direction, intl, maximum, minimum, mode,
      })}
      <Badge color="mid">{ruleId}</Badge>
    </CardText>
  );
}

TradeRuleCardText.propTypes = {
  tradeRule: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  direction: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  propertyTitle: PropTypes.string.isRequired,
};

export default TradeRuleCardText;
